import React, { useState } from 'react';
import { ReferMeRequest } from '../types/ReferMeTypes';
import { useAuth } from '../contexts/AuthContext';
import CloseIcon from '@mui/icons-material/Close';
import VerifiedIcon from '@mui/icons-material/Verified';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';

interface GetReferredModalProps {
  isOpen: boolean;
  onClose: () => void;
  vacancyId: string;
  vacancyTitle: string;
  companyName: string;
  darkMode: boolean;
}

interface MockReferrer {
  id: string;
  name: string;
  role: string;
  avatar: string;
}

const MOCK_REFERRERS: MockReferrer[] = [
  { id: 'ref1', name: 'Alex Chen', role: 'Senior Engineer', avatar: 'alex' },
  { id: 'ref2', name: 'Sarah Kim', role: 'Engineering Manager', avatar: 'sarah' },
  { id: 'ref3', name: 'James Wilson', role: 'Staff Engineer', avatar: 'james' },
];

const GetReferredModal: React.FC<GetReferredModalProps> = ({
  isOpen,
  onClose,
  vacancyId,
  vacancyTitle,
  companyName,
  darkMode,
}) => {
  const { user } = useAuth();
  const [step, setStep] = useState<1 | 2>(1);
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [linkedin, setLinkedin] = useState('');
  const [skills, setSkills] = useState('');
  const [pitch, setPitch] = useState('');
  const [error, setError] = useState('');
  const [requestedIds, setRequestedIds] = useState<string[]>([]);
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const handleStep1Submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      setError('Name and email are required.');
      return;
    }
    setError('');
    setStep(2);
  };

  const handleRequestReferral = (referrer: MockReferrer) => {
    const request: ReferMeRequest = {
      id: `rm_${Date.now()}_${referrer.id}`,
      vacancy_id: vacancyId,
      vacancy_title: vacancyTitle,
      company_name: companyName,
      candidate_id: user?.id || 'anonymous',
      candidate_name: name,
      candidate_email: email,
      candidate_linkedin: linkedin || undefined,
      pitch,
      skills: skills.split(',').map(s => s.trim()).filter(Boolean),
      status: 'pending',
      matched_referrer_id: referrer.id,
      matched_referrer_name: referrer.name,
      created_at: new Date().toISOString(),
    };

    // Save to localStorage
    const stored: ReferMeRequest[] = JSON.parse(localStorage.getItem('refer_me_requests') || '[]');
    stored.unshift(request);
    localStorage.setItem('refer_me_requests', JSON.stringify(stored));

    setRequestedIds(prev => [...prev, referrer.id]);
  };

  const handleSubmitWithoutReferrer = () => {
    const request: ReferMeRequest = {
      id: `rm_${Date.now()}`,
      vacancy_id: vacancyId,
      vacancy_title: vacancyTitle,
      company_name: companyName,
      candidate_id: user?.id || 'anonymous',
      candidate_name: name,
      candidate_email: email,
      candidate_linkedin: linkedin || undefined,
      pitch,
      skills: skills.split(',').map(s => s.trim()).filter(Boolean),
      status: 'pending',
      created_at: new Date().toISOString(),
    };

    const stored: ReferMeRequest[] = JSON.parse(localStorage.getItem('refer_me_requests') || '[]');
    stored.unshift(request);
    localStorage.setItem('refer_me_requests', JSON.stringify(stored));

    setSuccess(true);
  };

  const handleClose = () => {
    setStep(1);
    setError('');
    setRequestedIds([]);
    setSuccess(false);
    onClose();
  };

  return (
    <div className="modal-backdrop" onClick={handleClose}>
      <div className={`modal-content get-referred-modal ${darkMode ? 'dark' : ''}`} onClick={e => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={handleClose}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </button>

        {success ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <PersonSearchIcon sx={{ fontSize: 48, color: '#4f46e5', marginBottom: '12px' }} />
            <h2>Request Submitted!</h2>
            <p style={{ color: 'var(--text-secondary)', marginTop: '8px' }}>
              Your referral request for <strong>{vacancyTitle}</strong> at <strong>{companyName}</strong> has been submitted.
              {requestedIds.length > 0
                ? ' The referrers you selected will be notified.'
                : ' We will match you with a verified employee.'}
            </p>
            <button className="btn-primary" onClick={handleClose} style={{ marginTop: '20px', padding: '10px 24px' }}>
              Done
            </button>
          </div>
        ) : (
          <>
            <h2>Get Referred</h2>
            <p className="modal-subtitle">{vacancyTitle} at {companyName}</p>

            {/* Step indicator */}
            <div className="step-indicator">
              <div className={`step-dot ${step >= 1 ? 'active' : ''}`} />
              <div className={`step-dot ${step >= 2 ? 'active' : ''}`} />
            </div>

            {step === 1 && (
              <form onSubmit={handleStep1Submit} className="referral-modal">
                {error && <div className="modal-error">{error}</div>}
                <div className="form-group">
                  <label>Your Name *</label>
                  <input
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Full name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>LinkedIn Profile</label>
                  <input
                    type="url"
                    value={linkedin}
                    onChange={e => setLinkedin(e.target.value)}
                    placeholder="https://linkedin.com/in/yourprofile"
                  />
                </div>
                <div className="form-group">
                  <label>Skills (comma-separated)</label>
                  <input
                    type="text"
                    value={skills}
                    onChange={e => setSkills(e.target.value)}
                    placeholder="React, TypeScript, Node.js"
                  />
                </div>
                <div className="form-group">
                  <label>Short Pitch</label>
                  <textarea
                    value={pitch}
                    onChange={e => setPitch(e.target.value)}
                    placeholder="Why are you a great fit for this role?"
                    rows={3}
                  />
                </div>
                <button type="submit" className="btn-primary submit-referral-btn">
                  Next: Find Referrers
                </button>
              </form>
            )}

            {step === 2 && (
              <div>
                <h3 style={{ fontSize: '1rem', margin: '0 0 4px' }}>Verified Employees at {companyName}</h3>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: '0 0 12px' }}>
                  Request a referral from a verified employee. They will be notified with your profile.
                </p>

                <div className="referrer-list">
                  {MOCK_REFERRERS.map(referrer => (
                    <div key={referrer.id} className="referrer-card">
                      <img
                        src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${referrer.avatar}`}
                        alt={referrer.name}
                        className="referrer-avatar"
                      />
                      <div className="referrer-info">
                        <h4>
                          {referrer.name}
                          <VerifiedIcon sx={{ fontSize: 14, color: '#1976d2', marginLeft: '4px', verticalAlign: 'middle' }} />
                        </h4>
                        <p>{referrer.role} at {companyName}</p>
                      </div>
                      <button
                        className={`btn-primary btn-sm referrer-request-btn`}
                        disabled={requestedIds.includes(referrer.id)}
                        onClick={() => handleRequestReferral(referrer)}
                      >
                        {requestedIds.includes(referrer.id) ? 'Requested' : 'Request'}
                      </button>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                  <button
                    className="btn-outline"
                    onClick={() => setStep(1)}
                    style={{ flex: 1, padding: '10px', justifyContent: 'center' }}
                  >
                    Back
                  </button>
                  <button
                    className="btn-primary"
                    onClick={requestedIds.length > 0 ? handleClose : handleSubmitWithoutReferrer}
                    style={{ flex: 1, padding: '10px' }}
                  >
                    {requestedIds.length > 0 ? 'Done' : 'Submit Without Referrer'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default GetReferredModal;
